<template>
  <div class="scene">
    <div id="eucore-scene-editor" class="eucore-editor">
      <!-- 形象选择框（对齐 AS3 AdjustBox/AdjustFrame，移植自 eu-core editor-test.html）
           引擎出站 scene.bundleSelected(60,40)/bundleUnselected(60,41) 驱动显隐与定位 -->
      <div class="sel-layer">
        <div class="sel-box" v-show="selVisible && !isMultiSel" :style="selBoxStyle" @pointerdown="onSelPointerDown">
          <!-- A点(origin拷贝)选中时只保留移动：起点只允许改位置 -->
          <template v-if="!isOriginSel">
            <!-- 四角热区：缩放手柄常驻角点；悬停角区时沿对角线外弹出旋转图标（对齐AS3角点旋转交互） -->
            <div class="sel-corner nw">
              <div class="sel-handle" data-action="scale"></div>
              <div class="sel-rot" data-action="rotate" title="旋转"></div>
            </div>
            <div class="sel-corner ne">
              <div class="sel-handle" data-action="scale"></div>
              <div class="sel-rot" data-action="rotate" title="旋转"></div>
            </div>
            <div class="sel-corner se">
              <div class="sel-handle" data-action="scale"></div>
              <div class="sel-rot" data-action="rotate" title="旋转"></div>
            </div>
            <div class="sel-corner sw">
              <div class="sel-handle" data-action="scale"></div>
              <div class="sel-rot" data-action="rotate" title="旋转"></div>
            </div>
            <!-- 图标对齐 AS3 ui 图集：adjust_delete/adjust_reverse/adjust_reset/action_sequence_frame -->
            <button class="sel-btn flip" data-action="flip" title="翻转"></button>
            <button class="sel-btn reset" data-action="reset" title="重置"></button>
            <button class="sel-btn frames" v-if="selIsFrames" data-action="frames" title="序列帧设置"></button>
            <button class="sel-btn delete" data-action="delete" title="删除"></button>
          </template>
        </div>
        <!-- 多选（框选/Shift点选）：对齐AS3 AdjustBox.show——每个选中项一个框，只有边框无操作按钮，拖任一框整体移动 -->
        <div class="sel-box multi" v-for="f in multiFrames" :key="'m' + f.alias"
             v-show="selVisible && isMultiSel" :style="multiFrameStyle(f)" @pointerdown="onSelPointerDown"></div>
        <!-- 动作/形象编辑部件选中框（对齐 AS3 ActionEditBox/BunldeEditBox）：引擎 scene.partSelected(60,64) 推几何，
             手柄/控件经 scene.partTransform(60,65) 回传引擎换算为移动/缩放/旋转/斜切/中心点写帧。
             mode=='bundle' 时引擎下发 handles/ctrls/slots 白名单：只显示旋转/移动柄+组装点+重置按钮 -->
        <div class="peb" v-show="peb.show">
          <svg class="peb-svg" :viewBox="pebViewBox" preserveAspectRatio="none">
            <polygon class="peb-border" :points="pebQuadPoints" />
            <line v-if="pebShowHandle('rotate')" class="peb-guide" :x1="peb.d[0]" :y1="peb.d[1]" :x2="pebRotatePos[0]" :y2="pebRotatePos[1]" />
            <line v-if="pebShowHandle('move')" class="peb-guide" :x1="pebRightMid[0]" :y1="pebRightMid[1]" :x2="pebMovePos[0]" :y2="pebMovePos[1]" />
          </svg>
          <div class="peb-h corner" v-for="(p, k) in pebCornerPos" :key="'c'+k"
               v-if="pebShowHandle(k)"
               :style="{ left: p[0] + 'px', top: p[1] + 'px' }"
               :data-handle="k" @pointerdown="onPebPointerDown"></div>
          <div class="peb-h edge" v-for="(p, k) in pebEdgePos" :key="'e'+k"
               v-if="pebShowHandle(k)"
               :style="{ left: p[0] + 'px', top: p[1] + 'px' }"
               :data-handle="k" @pointerdown="onPebPointerDown"></div>
          <div class="peb-h rotate" v-if="pebShowHandle('rotate')" title="旋转"
               :style="{ left: pebRotatePos[0] + 'px', top: pebRotatePos[1] + 'px' }"
               data-handle="rotate" @pointerdown="onPebPointerDown"></div>
          <div class="peb-h move" v-if="pebShowHandle('move')" title="移动"
               :style="{ left: pebMovePos[0] + 'px', top: pebMovePos[1] + 'px' }"
               data-handle="move" @pointerdown="onPebPointerDown"></div>
          <div class="peb-h center" v-if="peb.showCenter && pebShowHandle('center')" title="中心点"
               :style="{ left: peb.center[0] + 'px', top: peb.center[1] + 'px' }"
               data-handle="center" @pointerdown="onPebPointerDown"></div>
          <!-- 形象编辑组装点按钮（对齐AS3 BunldeEditBox slot点）：type1=插位(KEYWAY,旋转中心) -->
          <div class="peb-h slot" v-for="s in peb.slots" :key="'s'+s.id"
               :class="{ keyway: s.type === 1 }" :title="s.type === 1 ? '插位' : '槽位'"
               :style="{ left: s.x + 'px', top: s.y + 'px' }"
               data-handle="slot" :data-slot="s.id" @pointerdown="onPebPointerDown"></div>
          <div class="peb-ctrl" v-if="pebShowCtrl()" :style="{ left: pebCtrlPos[0] + 'px', top: pebCtrlPos[1] + 'px' }">
            <!-- 图标用 AS3 ui 图集 action_moveUp/moveDown/binding/unBinding 的原图（26x26） -->
            <button v-if="pebShowCtrl('up')" class="peb-btn peb-up" title="上移层" @pointerdown="onPebPointerDown" data-handle="up"></button>
            <button v-if="pebShowCtrl('down')" class="peb-btn peb-down" title="下移层" @pointerdown="onPebPointerDown" data-handle="down"></button>
            <button v-if="pebShowCtrl('hold') && peb.type === 'PART'" class="peb-btn peb-hold" title="绑定道具" @pointerdown="onPebPointerDown" data-handle="hold"></button>
            <button v-if="pebShowCtrl('unhold') && peb.type === 'HOLDBUNDLE' && peb.editType !== 'EDITTYPE_PROPINIT'" class="peb-btn peb-unhold" title="解绑道具" @pointerdown="onPebPointerDown" data-handle="unhold"></button>
            <button v-if="pebShowCtrl('reset')" class="peb-btn peb-reset" title="重置" @pointerdown="onPebPointerDown" data-handle="reset"></button>
          </div>
        </div>
      </div>
    </div>
    <!-- 隐藏 shim：protocol.js 按 id 找 SCENE-SWF，要求 tagName=='OBJECT' 且有 .send 方法 -->
    <object id="SCENE-SWF" style="display:none;width:0;height:0"></object>
  </div>
</template>

<script>
  // eu-core 动画编辑器（替换原 Scene.swf）
  // 消息入站：protocol.js -> object#SCENE-SWF.send -> EuCoreEngine.sendMessage -> MessageAdapter
  // 消息出站：eu-core Protocol.sender -> window.onReceiverMessage（protocol.js 总线）
  export default {
    data () {
      return {
        editorId: null,
        selVisible: false,
        selBoxStyle: {},
        selInfo: null,
        selBase: null,
        selDrag: { dx: 0, dy: 0, ratio: 1, angle: 0 },
        // 多选框几何 [{alias,w,h,left,top,angle}]，单选时为空
        multiFrames: [],
        // 动作/形象编辑部件选中框（对齐 AS3 ActionEditBox/BunldeEditBox）：几何全部由引擎 60_64 推送
        // mode=='bundle'：handles/ctrls 白名单控制手柄显隐，slots=组装点按钮列表
        peb: { show: false, a: [0, 0], b: [0, 0], c: [0, 0], d: [0, 0], center: [0, 0], showCenter: false, type: '', editType: '', hasHold: false, isFreeform: false, mode: '', handles: null, ctrls: null, slots: [] },
        stageW: 800,
        stageH: 600
      }
    },
    computed: {
      // 引擎选择框回执的alias为R前缀时表示A点(origin拷贝)选中：选择框只做移动
      isOriginSel () {
        return !!(this.selInfo && this.selInfo.alias && this.selInfo.alias.charAt(0) === 'R')
      },
      // 单选且为序列帧形象时显示"序列帧设置"按钮（对齐AS3 AdjustBox mIsFramesBundle）
      selIsFrames () {
        const info = this.selInfo
        return !!(info && info.isFrames && !this.isOriginSel
          && (!info.aliases || info.aliases.length === 1))
      },
      isMultiSel () {
        return this.multiFrames.length > 1
      },
      // svg坐标=舞台逻辑像素，viewBox与容器1:1缩放
      pebViewBox () {
        return '0 0 ' + this.stageW + ' ' + this.stageH
      },
      // 边框四边形（A左上->B右上->D右下->C左下，对齐AS3 mBorder画线顺序）
      pebQuadPoints () {
        const p = this.peb
        return p.a[0] + ',' + p.a[1] + ' ' + p.b[0] + ',' + p.b[1] + ' ' + p.d[0] + ',' + p.d[1] + ' ' + p.c[0] + ',' + p.c[1]
      },
      // 四角变换手柄（a/b/c/d 对应 AS3 mA/mB/mC/mDTransformBtn）
      pebCornerPos () {
        const p = this.peb
        return { a: p.a, b: p.b, c: p.c, d: p.d }
      },
      // 四边中点缩放手柄（对齐AS3 mTop/mBottom/mLeft/mRightScaleBtn）
      pebEdgePos () {
        const p = this.peb
        const mid = (u, v) => [(u[0] + v[0]) / 2, (u[1] + v[1]) / 2]
        return { top: mid(p.a, p.b), right: mid(p.b, p.d), bottom: mid(p.c, p.d), left: mid(p.a, p.c) }
      },
      pebRightMid () {
        const p = this.peb
        return [(p.b[0] + p.d[0]) / 2, (p.b[1] + p.d[1]) / 2]
      },
      // 旋转柄：D角向外沿角平分方向偏移（对齐AS3 mDRotateGrp 相对D角+mBulge）
      pebRotatePos () {
        return this.outward(this.peb.d)
      },
      // 移动柄：右边中点向外偏移（对齐AS3 mMoveGrp 相对右中点+mBulge）
      pebMovePos () {
        return this.outward(this.pebRightMid)
      },
      // 控制面板：四边形外接框左上外偏（对齐AS3 setContolPanelPoint: leftTop-mBulge）
      pebCtrlPos () {
        const p = this.peb
        const minX = Math.min(p.a[0], p.b[0], p.c[0], p.d[0])
        const minY = Math.min(p.a[1], p.b[1], p.c[1], p.d[1])
        return [minX - 4, minY - 36]
      }
    },
    methods: {
      initEuCore () {
        const engine = window.EuCoreEngine && window.EuCoreEngine.getInstance()
        if (!engine) {
          console.error('[Scene] EuCoreEngine 未加载（static/eucore/eucore.js）')
          return
        }
        // API走同源相对路径：/api/storage 由 dev-server 代理到 ai.eeeuy.com
        // （默认 http://localhost:9090 只用于 editor-test 独立测试页）
        if (window.EuCoreEngine.setAPIUrl) {
          window.EuCoreEngine.setAPIUrl('')
        }
        // 后端接口需要 Authorization：token 存在 localStorage.login_status.tokenId
        this.syncEuCoreToken()
        const container = document.getElementById('eucore-scene-editor')
        this.editorId = engine.createEditor('eucore-scene-editor', 'movie', {
          width: container.clientWidth || 800,
          height: container.clientHeight || 600,
          backgroundColor: 0xffffff
        })
        this.bootstrapEmptyMovie()
      },
      syncEuCoreToken () {
        // App.vue 心跳会刷新 login_status.tokenId，每次调用都重读保证 token 最新
        if (!window.EuCoreEngine || !window.EuCoreEngine.setAPIToken) return
        try {
          const loginStatus = JSON.parse(localStorage.getItem('login_status') || 'null')
          const token = loginStatus && loginStatus.tokenId
          if (token && token !== window.EuCoreEngine.getAPIToken()) {
            window.EuCoreEngine.setAPIToken(token)
          }
        } catch (e) { /* ignore */ }
      },
      bootstrapEmptyMovie () {
        // 与 editor-test.html 的 initEmptyMovie 一致：1 page + 1 step
        // 后续真实剧本加载消息到达时会覆盖
        try {
          const result = window.EuCore.EuCoreFactory.getInstance(this.editorId)
          if (!result || !result.instance) return
          const res = window.EuCore.resource
          const script = new res.MovieScript()
          const page = new res.Page()
          page.steps.push(new res.Step())
          script.pages.push(page)
          const editor = result.instance.editor || result.instance
          // 延迟自举：若此期间已加载/正在加载真实剧本（1_22/1_12 标签消息），则不覆盖
          setTimeout(() => {
            if ((editor.hasMovieData && editor.hasMovieData())
              || (editor.isLoadingMovie && editor.isLoadingMovie())) {
              return
            }
            editor.load({ movieScript: script }).then(() => {
            }).catch((e) => {
              console.error('[Scene] empty movie load failed:', e)
            })
          }, 300)
        } catch (e) {
          console.error('[Scene] bootstrapEmptyMovie error:', e)
        }
      },
      installProtocolShim () {
        // protocol.js 按 DOM id 'SCENE-SWF' 找 OBJECT 并调 .send(...positional args)
        const swfShim = document.getElementById('SCENE-SWF')
        swfShim.send = (packageId, senderId, receiverId, timeFlag, sessionId, messageId, protoBytes, data) => {
          // sessionId=60 是引擎出站的选择框回执（scene.bundleSelected 等），
          // 由本组件的选择框层消费；不必回送引擎（senderId=EU_CORE，回送也会被适配层丢弃）
          if (sessionId === 60) {
            this.onSceneDataMessage(messageId, data)
            return
          }
          const engine = window.EuCoreEngine && window.EuCoreEngine.getInstance()
          if (!engine) return
          // 心跳刷新过 login_status 时同步最新 token（切动画加载剧本依赖 Authorization）
          this.syncEuCoreToken()
          engine.sendMessage({
            packageId: packageId,
            senderId: senderId,
            senderName: '',
            receiverId: receiverId,
            receiverName: 'SCENE',
            timeFlag: timeFlag,
            sessionId: sessionId,
            messageId: messageId,
            protoBytes: protoBytes,
            data: data
          })
        }
      },

      //=============================
      // 形象选择框（HTML覆盖层，对齐 AS3 AdjustBox）
      // 引擎 -> 页面：(60,40) bundleSelected {alias,id,vertexs[4],center,rotation,scale,facing}
      //              (60,41) bundleUnselected
      // 页面 -> 引擎：(60,43)移动 (60,44)缩放 (60,45)旋转 (60,46)翻转 (60,47)取消预览 (60,21)删除
      //=============================

      // 双击形象进入动作编辑（对齐 AS3 AdjustFrame.onFrameBtnClick）：
      // 双击判定要求两次点击都落在选中框上——形象未选中时第一次点击只是选中，
      // 选中框出现后落下的第二次点击仅算框上的第一次点击，不会触发进入
      requestEnterActionEdit () {
        if (!this.selInfo || !this.selInfo.alias) return
        this.sendSceneMessage(60, { alias: this.selInfo.alias })
      },

      // 引擎出站 sessionId=60 消息
      onSceneDataMessage (messageId, data) {
        if (messageId === 40) {
          this.showSelection(data)
        } else if (messageId === 41) {
          this.hideSelection()
        } else if (messageId === 64) {
          this.handlePartSelected(data)
        }
      },

      // 页面 -> 引擎的选择框消息，data 直接携带参数（绕过 proto 序列化）
      sendSceneMessage (messageId, data) {
        const engine = window.EuCoreEngine && window.EuCoreEngine.getInstance()
        if (!engine) return
        engine.sendMessage({
          packageId: 6,
          senderId: 8,
          senderName: 'SCENE',
          receiverId: 8,
          receiverName: 'SCENE',
          timeFlag: Date.now(),
          sessionId: 60,
          messageId: messageId,
          protoBytesType: '',
          protoBytes: [],
          data: data
        })
      },

      // vertexs/center 是画布(stage)像素坐标，canvas 铺满 .eucore-editor 即容器坐标
      // 四顶点(左上/右上/右下/左下)+中心 -> 未旋转框的尺寸/左上角 + 旋转角
      quadBox (v, center) {
        const w = Math.hypot(v[1].x - v[0].x, v[1].y - v[0].y)
        const h = Math.hypot(v[3].x - v[0].x, v[3].y - v[0].y)
        return {
          w: w,
          h: h,
          left: center.x - w / 2,
          top: center.y - h / 2,
          angle: Math.atan2(v[1].y - v[0].y, v[1].x - v[0].x)
        }
      },

      showSelection (info) {
        if (!info || !info.vertexs || info.vertexs.length < 4) return
        this.selInfo = info
        this.selBase = this.quadBox(info.vertexs, info.center)
        const frames = (info.frames || []).filter(f => f && f.vertexs && f.vertexs.length >= 4)
        this.multiFrames = frames.length > 1
          ? frames.map(f => Object.assign({ alias: f.alias }, this.quadBox(f.vertexs, f.center)))
          : []
        this.selDrag = { dx: 0, dy: 0, ratio: 1, angle: 0 }
        this.updateSelBoxStyle()
      },

      // 多选框：只跟随拖动平移
      multiFrameStyle (f) {
        const d = this.selDrag
        return {
          width: f.w + 'px',
          height: f.h + 'px',
          left: f.left + 'px',
          top: f.top + 'px',
          transform: 'translate(' + d.dx + 'px,' + d.dy + 'px) rotate(' + f.angle + 'rad)'
        }
      },

      // 当前拖动作用的别名：多选=全部选中项，单选=选中项
      selTargetAliases () {
        if (this.isMultiSel) return this.multiFrames.map(f => f.alias)
        return this.selInfo ? [this.selInfo.alias] : []
      },

      hideSelection () {
        this.selInfo = null
        this.selBase = null
        this.multiFrames = []
        this._selDragState = null
        this._selLastBoxClick = 0
        this.removeSelDragListeners()
        this.selVisible = false
      },

      updateSelBoxStyle () {
        const b = this.selBase
        const d = this.selDrag
        if (!b) {
          this.selVisible = false
          return
        }
        // 缩放预览改框本身的宽高（绕中心），不用CSS scale()——
        // 手柄/按钮/旋转柄都是固定px偏移，框变大时它们大小与相对位置保持不变（对齐AS3 AdjustFrame按钮不随形象缩放）
        const w = b.w * d.ratio
        const h = b.h * d.ratio
        this.selBoxStyle = {
          width: w + 'px',
          height: h + 'px',
          left: (b.left + (b.w - w) / 2) + 'px',
          top: (b.top + (b.h - h) / 2) + 'px',
          transform: 'translate(' + d.dx + 'px,' + d.dy + 'px) rotate(' + (b.angle + d.angle) + 'rad)'
        }
        this.selVisible = true
      },

      onSelPointerDown (e) {
        if (!this.selInfo || e.button !== 0) return
        const action = (e.target.dataset && e.target.dataset.action) || 'move'
        if (action === 'flip') {
          this.sendSceneMessage(46, { alias: this.selInfo.alias })
          return
        }
        if (action === 'delete') {
          this.sendSceneMessage(21, { alias: this.selInfo.alias })
          return
        }
        if (action === 'reset') {
          this.sendSceneMessage(48, { alias: this.selInfo.alias })
          return
        }
        // 序列帧设置：引擎弹 6_53 -> App.vue pop_block
        if (action === 'frames') {
          this.sendSceneMessage(59, { alias: this.selInfo.alias })
          return
        }
        e.preventDefault()
        e.stopPropagation()
        const rect = this.$el.querySelector('.eucore-editor').getBoundingClientRect()
        const c = { x: rect.left + this.selInfo.center.x, y: rect.top + this.selInfo.center.y }
        this._selDragState = {
          action: action,
          startX: e.clientX,
          startY: e.clientY,
          startDist: Math.max(1, Math.hypot(e.clientX - c.x, e.clientY - c.y)),
          startAngle: Math.atan2(e.clientY - c.y, e.clientX - c.x),
          center: c,
          dx: 0, dy: 0, ratio: 1, angle: 0,
          sentDx: 0, sentDy: 0, sentRatio: 1, sentAngle: 0, previewPending: false
        }
        document.addEventListener('pointermove', this.onSelDragMove)
        document.addEventListener('pointerup', this.onSelDragEnd)
        document.addEventListener('pointercancel', this.onSelDragCancel)
      },

      // 拖拽中：选择框用 CSS 变换预览，增量以 preview:true 发给引擎（只改舞台显示不写剧本）
      onSelDragMove (e) {
        const s = this._selDragState
        if (!s) return
        if (s.action === 'move') {
          s.dx = e.clientX - s.startX
          s.dy = e.clientY - s.startY
        } else if (s.action === 'scale') {
          s.ratio = Math.max(0.05, Math.hypot(e.clientX - s.center.x, e.clientY - s.center.y) / s.startDist)
        } else if (s.action === 'rotate') {
          s.angle = Math.atan2(e.clientY - s.center.y, e.clientX - s.center.x) - s.startAngle
        }
        this.selDrag = { dx: s.dx, dy: s.dy, ratio: s.ratio, angle: s.angle }
        this.updateSelBoxStyle()
        if (!s.previewPending) {
          s.previewPending = true
          requestAnimationFrame(this.sendSelTransformPreview)
        }
      },

      // 发送自上次以来的增量预览
      sendSelTransformPreview () {
        const s = this._selDragState
        if (!s || !this.selInfo) return
        s.previewPending = false
        if (s.action === 'move' && (s.dx !== s.sentDx || s.dy !== s.sentDy)) {
          this.selTargetAliases().forEach(alias => {
            this.sendSceneMessage(43, { alias: alias, dx: s.dx - s.sentDx, dy: s.dy - s.sentDy, preview: true })
          })
          s.sentDx = s.dx
          s.sentDy = s.dy
        } else if (s.action === 'scale' && s.ratio !== s.sentRatio) {
          this.sendSceneMessage(44, { alias: this.selInfo.alias, ratio: s.ratio / s.sentRatio, preview: true })
          s.sentRatio = s.ratio
        } else if (s.action === 'rotate' && s.angle !== s.sentAngle) {
          this.sendSceneMessage(45, { alias: this.selInfo.alias, angle: s.angle - s.sentAngle, preview: true })
          s.sentAngle = s.angle
        }
      },

      removeSelDragListeners () {
        document.removeEventListener('pointermove', this.onSelDragMove)
        document.removeEventListener('pointerup', this.onSelDragEnd)
        document.removeEventListener('pointercancel', this.onSelDragCancel)
      },

      // 拖拽取消（指针移出窗口等）：通知引擎按剧本重绘，撤销预览变形
      onSelDragCancel () {
        this.removeSelDragListeners()
        if (this._selDragState) {
          this._selDragState = null
          this.sendSceneMessage(47, {})
          if (this.selInfo) {
            this.selDrag = { dx: 0, dy: 0, ratio: 1, angle: 0 }
            this.updateSelBoxStyle()
          }
        }
      },

      // 松手：提交一次完整操作（对应一步撤销），引擎重绘后会再发 bundleSelected 刷新框位置
      onSelDragEnd () {
        this.removeSelDragListeners()
        const s = this._selDragState
        this._selDragState = null
        if (!s || !this.selInfo) return
        const alias = this.selInfo.alias
        if (s.action === 'move' && (Math.abs(s.dx) > 1 || Math.abs(s.dy) > 1)) {
          this.selTargetAliases().forEach(a => {
            this.sendSceneMessage(43, { alias: a, dx: s.dx, dy: s.dy })
          })
        } else if (s.action === 'scale' && Math.abs(s.ratio - 1) > 0.005) {
          this.sendSceneMessage(44, { alias: alias, ratio: s.ratio })
        } else if (s.action === 'rotate' && Math.abs(s.angle) > 0.002) {
          this.sendSceneMessage(45, { alias: alias, angle: s.angle })
        } else {
          // 未产生变化，恢复选择框
          this.selDrag = { dx: 0, dy: 0, ratio: 1, angle: 0 }
          this.updateSelBoxStyle()
          if (s.action === 'move' && !this.isMultiSel) {
            const now = Date.now()
            if (this._selLastBoxClick && now - this._selLastBoxClick <= 300) {
              this._selLastBoxClick = 0
              this.requestEnterActionEdit()
            } else {
              this._selLastBoxClick = now
            }
          }
        }
      },

      //=============================
      // 动作编辑部件选中框（对齐 AS3 ActionEditBox）
      // 引擎 -> 页面：(60,64) {sel,a,b,c,d,center,type,editType,hasHold,isFreeform,showCenter}
      // 页面 -> 引擎：(60,65) {handle,gx,gy,phase:'start'|'move'|'end'|'click'}
      //   舞台逻辑像素与容器CSS像素1:1（canvas铺满.eucore-editor）
      //=============================

      // 60_64：部件选中框几何更新
      handlePartSelected (data) {
        let info = data
        if (typeof info === 'string') {
          try { info = JSON.parse(info) } catch (e) { return }
        }
        if (!info || !info.sel) {
          this.peb = Object.assign({}, this.peb, { show: false })
          return
        }
        const container = this.$el.querySelector('.eucore-editor')
        if (container) {
          this.stageW = container.clientWidth
          this.stageH = container.clientHeight
        }
        this.peb = Object.assign({}, this.peb, {
          show: true,
          a: info.a, b: info.b, c: info.c, d: info.d,
          center: info.center || [0, 0],
          showCenter: !!info.showCenter,
          type: info.type || '',
          editType: info.editType || '',
          hasHold: !!info.hasHold,
          isFreeform: !!info.isFreeform,
          //形象编辑：handles/ctrls白名单 + slots组装点（null=默认全部手柄，动作编辑兼容）
          mode: info.mode || '',
          handles: Array.isArray(info.handles) ? info.handles : null,
          ctrls: Array.isArray(info.ctrls) ? info.ctrls : null,
          slots: Array.isArray(info.slots) ? info.slots : []
        })
      },

      // 手柄显隐：引擎下发handles白名单时只显示名单内手柄（null=默认全显，动作编辑兼容）
      pebShowHandle (name) {
        const h = this.peb.handles
        if (!h) return true
        return h.indexOf(name) >= 0
      },

      // 控件按钮显隐：引擎下发ctrls白名单时按名单（null=默认显示层叠/绑定按钮组）
      pebShowCtrl (name) {
        const c = this.peb.ctrls
        if (c) {
          return name ? c.indexOf(name) >= 0 : c.length > 0
        }
        // 默认控件集（动作编辑）：up/down/hold/unhold（reset不在默认集内）
        if (name === undefined) return true
        return ['up', 'down', 'hold', 'unhold'].indexOf(name) >= 0
      },

      // 从点沿"四边形中心->该点"方向外推len px（旋转/移动柄挂点）
      outward (pos, len) {
        const p = this.peb
        const cx = (p.a[0] + p.b[0] + p.c[0] + p.d[0]) / 4
        const cy = (p.a[1] + p.b[1] + p.c[1] + p.d[1]) / 4
        const dx = pos[0] - cx
        const dy = pos[1] - cy
        const dist = Math.max(1, Math.hypot(dx, dy))
        const l = len || 16
        return [pos[0] + dx / dist * l, pos[1] + dy / dist * l]
      },

      // 手柄按下：控件按钮发click，变形手柄开始手势(start->move->end)
      onPebPointerDown (e) {
        if (e.button !== 0 || !this.peb.show) return
        const handle = (e.target.dataset && e.target.dataset.handle) || ''
        if (!handle) return
        e.preventDefault()
        e.stopPropagation()
        if (handle === 'up' || handle === 'down' || handle === 'hold' || handle === 'unhold' || handle === 'reset') {
          this.sendSceneMessage(65, { handle: handle, phase: 'click' })
          return
        }
        const rect = this.$el.querySelector('.eucore-editor').getBoundingClientRect()
        this._pebDragState = {
          handle: handle,
          slot: (e.target.dataset && e.target.dataset.slot) || '',
          rect: rect,
          gx: e.clientX - rect.left,
          gy: e.clientY - rect.top,
          movePending: false
        }
        this.sendSceneMessage(65, { handle: handle, slot: this._pebDragState.slot, gx: this._pebDragState.gx, gy: this._pebDragState.gy, phase: 'start' })
        document.addEventListener('pointermove', this.onPebDragMove)
        document.addEventListener('pointerup', this.onPebDragEnd)
        document.addEventListener('pointercancel', this.onPebDragEnd)
      },

      onPebDragMove (e) {
        const s = this._pebDragState
        if (!s) return
        s.gx = e.clientX - s.rect.left
        s.gy = e.clientY - s.rect.top
        if (!s.movePending) {
          s.movePending = true
          requestAnimationFrame(this.sendPebDragMove)
        }
      },

      sendPebDragMove () {
        const s = this._pebDragState
        if (!s) return
        s.movePending = false
        this.sendSceneMessage(65, { handle: s.handle, slot: s.slot, gx: s.gx, gy: s.gy, phase: 'move' })
      },

      onPebDragEnd (e) {
        const s = this._pebDragState
        this._pebDragState = null
        document.removeEventListener('pointermove', this.onPebDragMove)
        document.removeEventListener('pointerup', this.onPebDragEnd)
        document.removeEventListener('pointercancel', this.onPebDragEnd)
        if (!s) return
        const gx = e.clientX - s.rect.left
        const gy = e.clientY - s.rect.top
        this.sendSceneMessage(65, { handle: s.handle, slot: s.slot, gx: gx, gy: gy, phase: 'end' })
      }
    },
    mounted () {
      this.installProtocolShim()
      this.initEuCore()
    },
    deactivated () {
      // App.vue 的 router-view 带 keep-alive：路由离开时组件不销毁、beforeDestroy 不触发。
      // 在这里停播放并取消形象选中；下次进入由 1_12 标签消息重置到第一页第一步
      this.hideSelection()
      this.peb = Object.assign({}, this.peb, { show: false })
      try {
        const result = window.EuCore && this.editorId && window.EuCore.EuCoreFactory.getInstance(this.editorId)
        const editor = result && (result.instance.editor || result.instance)
        if (editor) {
          if (editor.setSelection) editor.setSelection(null)
          if (editor.stop) editor.stop()
        }
      } catch (e) {}
    },
    beforeDestroy () {
      this.removeSelDragListeners()
      const engine = window.EuCoreEngine && window.EuCoreEngine.getInstance()
      if (engine && this.editorId) {
        engine.destroyInstance(this.editorId)
        this.editorId = null
      }
    }
  }
</script>

<style lang="stylus">
  .scene
    position: absolute
    top: 0
    right: 0
    bottom: 0
    left: 0
  .eucore-editor
    position: absolute
    top: 0
    right: 0
    bottom: 0
    left: 0
    /* canvas向上取整可能比容器大<1px，裁掉避免压到时间轴；底色用暗化同色兜底细缝 */
    overflow: hidden
    background: #333
    canvas
      display: block
  /* 形象选择框覆盖层：坐标与画布像素 1:1（canvas 铺满容器） */
  .sel-layer
    position: absolute
    top: 0
    right: 0
    bottom: 0
    left: 0
    /* 空白处点击穿透到画布，由引擎判断选中/取消 */
    pointer-events: none
    z-index: 10
  .sel-box
    position: absolute
    box-sizing: border-box
    border: 1.5px dashed #29b6f6
    pointer-events: auto
    cursor: move
    transform-origin: 50% 50%
    &.multi
      border-style: solid
      border-width: 1px
  /* 四角热区：44px覆盖角点内外，手柄/图标都在区内，从手柄滑向图标不离开:hover */
  .sel-corner
    position: absolute
    width: 44px
    height: 44px
    &.nw
      left: -24px
      top: -24px
      .sel-handle
        left: 24px
        top: 24px
        cursor: nwse-resize
      .sel-rot
        left: 0
        top: 0
    &.ne
      right: -24px
      top: -24px
      .sel-handle
        left: 20px
        top: 24px
        cursor: nesw-resize
      .sel-rot
        right: 0
        top: 0
    &.se
      right: -24px
      bottom: -24px
      .sel-handle
        left: 20px
        top: 20px
        cursor: nwse-resize
      .sel-rot
        right: 0
        bottom: 0
    &.sw
      left: -24px
      bottom: -24px
      .sel-handle
        left: 24px
        top: 20px
        cursor: nesw-resize
      .sel-rot
        left: 0
        bottom: 0
    &:hover
      .sel-rot
        opacity: 1
        pointer-events: auto
  .sel-handle
    position: absolute
    width: 12px
    height: 12px
    margin: -6px 0 0 -6px
    background: #fff
    border: 2px solid #29b6f6
    border-radius: 2px
    box-sizing: border-box
    z-index: 1
  /* 角点旋转图标：与动作编辑器旋转柄同一图（peb/rotate.png），悬停角区浮出 */
  .sel-rot
    position: absolute
    width: 18px
    height: 18px
    background-image: url('/static/img/peb/rotate.png')
    background-size: contain
    background-repeat: no-repeat
    opacity: 0
    pointer-events: none
    transition: opacity 0.15s
    cursor: grab
  /* 操作按钮对齐 AS3 AdjustFrame.putButtons：顶边上方22px，从右角起13px步进38px
     顺序：删除->翻转->重置->序列帧设置；图标取 ui.png 图集（26px，三态） */
  .sel-btn
    position: absolute
    top: -36px
    width: 26px
    height: 26px
    padding: 0
    border: none
    cursor: pointer
    background-color: transparent
    background-image: url(./ui.png)
    background-repeat: no-repeat
    &.delete
      right: 0
      background-position: -129px -196px
      &:hover
        background-position: -156px -168px
      &:active
        background-position: -129px -224px
    &.flip
      right: 38px
      background-position: -72px -170px
      &:hover
        background-position: -128px -168px
      &:active
        background-position: -130px -140px
    &.reset
      right: 76px
      background-position: -73px -198px
      &:hover
        background-position: -100px -170px
      &:active
        background-position: -73px -226px
    &.frames
      right: 114px
      background-position: -162px -96px
      &:hover
        /* hover/click态图集是28px，居中取26px区域 */
        background-position: -44px -208px
      &:active
        background-position: -73px -141px
  /* 动作编辑部件选中框（对齐 AS3 ActionEditBox：黄边框0xFFFF99/引导线0xF49F00/红色手柄） */
  .peb
    position: absolute
    top: 0
    right: 0
    bottom: 0
    left: 0
    pointer-events: none
  .peb-svg
    position: absolute
    top: 0
    left: 0
    width: 100%
    height: 100%
    pointer-events: none
  .peb-border
    fill: none
    stroke: #ffff99
    stroke-width: 1
    vector-effect: non-scaling-stroke
  .peb-guide
    stroke: #f49f00
    stroke-width: 1
    vector-effect: non-scaling-stroke
  .peb-h
    position: absolute
    width: 9px
    height: 9px
    margin: -4.5px 0 0 -4.5px
    box-sizing: border-box
    pointer-events: auto
    &.corner
      background: #ff0000
      border: 1px solid #fff
      cursor: nwse-resize
    &.edge
      background: #fff
      border: 1px solid #ff0000
      cursor: ns-resize
    /* 旋转/移动/点按钮用 AS3 ui 图集原图（action_rotate/action_move/action_point/action_point_parent），
       与动作编辑器共用同一套样式 */
    &.rotate
      width: 16px
      height: 16px
      margin: -8px 0 0 -8px
      background-image: url('/static/img/peb/rotate.png')
      background-position: center
      background-size: contain
      background-repeat: no-repeat
      cursor: grab
    &.move
      width: 18px
      height: 18px
      margin: -9px 0 0 -9px
      background-image: url('/static/img/peb/move.png')
      background-position: center
      background-size: contain
      background-repeat: no-repeat
      cursor: move
    &.center
      width: 10px
      height: 10px
      margin: -5px 0 0 -5px
      background-image: url('/static/img/peb/point.png')
      background-position: center
      background-size: contain
      background-repeat: no-repeat
      cursor: crosshair
    &.slot
      width: 10px
      height: 10px
      margin: -5px 0 0 -5px
      background-image: url('/static/img/peb/point.png')
      background-position: center
      background-size: contain
      background-repeat: no-repeat
      cursor: move
    &.slot.keyway
      width: 12px
      height: 12px
      margin: -6px 0 0 -6px
      background-image: url('/static/img/peb/point_parent.png')
      cursor: move
  .peb-ctrl
    position: absolute
    display: flex
    pointer-events: auto
  .peb-btn
    width: 26px
    height: 26px
    margin-right: 6px
    padding: 0
    border: none
    background-color: transparent
    background-repeat: no-repeat
    background-position: center
    background-size: 26px 26px
    cursor: pointer
  .peb-up
    background-image: url('/static/img/peb/up.png')
    &:hover
      background-image: url('/static/img/peb/up_hover.png')
  .peb-down
    background-image: url('/static/img/peb/down.png')
    &:hover
      background-image: url('/static/img/peb/down_hover.png')
  .peb-hold
    background-image: url('/static/img/peb/hold.png')
    &:hover
      background-image: url('/static/img/peb/hold_hover.png')
  .peb-unhold
    background-image: url('/static/img/peb/unhold.png')
    &:hover
      background-image: url('/static/img/peb/unhold_hover.png')
  .peb-reset
    background-image: url('/static/img/peb/reset.png')
    &:hover
      background-image: url('/static/img/peb/reset_hover.png')
</style>



// WEBPACK FOOTER //
// Scene.vue?3b607ca2